import React, { useState, useRef } from "react";
import uuid from "react-native-uuid";
import {
  Text,
  View,
  ImageBackground,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ActivityIndicator,
  ToastAndroid,
  StyleSheet,
  Linking,
  Alert,
} from "react-native";
import { launchCamera } from "react-native-image-picker";
import CloseIcon from "@assets/svg/CloseIcon";
import Play from "@assets/svg/Play";
import Camera2 from "@assets/svg/Camera2";
import Microphone2 from "@assets/svg/Microphone2";
import t from "@constants/localization";
import colors from "@constants/color";
import checkPermissions from "./CheckPermissions";
import { uploadAttachments } from "@services/api";
import {
  pick,
  types,
  errorCodes,
  isErrorWithCode,
} from "@react-native-documents/picker";

const PATH = "screens/Wallet/v2/Components/UploadProof";

const UploadProof = ({ onSubmit }: any) => {
  const [proofMap, setProofMap] = useState([]);

  const remove = (itemIdx: number) => {
    if (itemIdx !== -1) {
      proofMap.splice(itemIdx, 1);
      setProofMap(proofMap.slice());
      onSubmit(proofMap.map((s) => s.url));
    }
  };

  const updateSrc = (newMap: any) => {
    setProofMap([...proofMap, newMap]);
  };

  const updateUrlRef: any = useRef();
  updateUrlRef.current = (id: any, url: any) => {
    const itemIdx = proofMap.findIndex((s) => s.id === id);
    if (itemIdx !== -1) {
      proofMap[itemIdx] = {
        ...proofMap[itemIdx],
        url,
      };
      setProofMap(proofMap.slice());
      onSubmit(proofMap.map((s) => s.url));
    }
  };

  const onImageSelect = async (response: any) => {
    try {
      if (response.didCancel) {
        return;
      } else if (response.error) {
        console.log("ImagePicker Error: ", response.error);
      }
      if (!response.type?.startsWith("image/")) {
        Alert.alert("Invalid File", "Please select a valid image file.");
        return;
      }
      const source = { uri: `${response.uri}` };
      const id = String(uuid.v1());
      updateSrc({ source, id, type: "image" });
      const data = new FormData();
      data.append("file", {
        name: response.fileName,
        type: response.type,
        uri: response.uri,
      });
      const { data: uploadData } = await uploadAttachments(data);
      updateUrlRef.current(id, uploadData.url);
    } catch (e) {
      console.log("upload image error", e);
      ToastAndroid.showWithGravity(
        t(`${PATH}/errorUploading`),
        ToastAndroid.SHORT,
        ToastAndroid.CENTER,
      );
    }
  };

  const onAttachmentUpload = async (type: string) => {
    try {
      await checkPermissions();
      if (proofMap.length > 3) {
        ToastAndroid.showWithGravity(
          t(`${PATH}/uploadLimit`),
          ToastAndroid.SHORT,
          ToastAndroid.CENTER,
        );
        return;
      }
      if (type == "camera") {
        launchCamera(
          {
            mediaType: "photo",
            maxHeight: 500,
            maxWidth: 700,
            quality: 0.4,
          },
          onImageSelect,
        );
      } else if (type == "audio") {
        pickAudioFile();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const pickAudioFile = async () => {
    try {
      const [res] = await pick({
        type: [types.audio],
        allowMultiSelection: false,
      });
      const source = { uri: `${res.uri}` };
      const id = String(uuid.v1());
      updateSrc({ source, id, type: "audio" });
      const data = new FormData();
      data.append("file", {
        name: res.name,
        type: res.type,
        uri: res.uri,
      });
      const { data: uploadData } = await uploadAttachments(data);
      updateUrlRef.current(id, uploadData.url);
    } catch (err) {
      if (isErrorWithCode(err) && err.code === errorCodes.OPERATION_CANCELED) {
        console.log("User cancelled the picker");
      } else {
        console.error(err);
      }
    }
  };

  return (
    <>
      <View style={styles.proofContainer}>
        {proofMap.map((proof, index) => {
          if (proof.type === "image") {
            return (
              <TouchableWithoutFeedback
                onPress={() => {
                  if (proof.url) {
                    Linking.openURL(proof.url);
                  }
                }}
              >
                <ImageBackground
                  key={index}
                  source={proof.url ? { uri: proof.url } : proof.source}
                  resizeMode="contain"
                  style={styles.imgBg}
                >
                  <TouchableOpacity
                    onPress={() => remove(index)}
                    style={styles.imgContainer}
                  >
                    <View>
                      <CloseIcon width={10} height={10} />
                      {proof.source && !proof.url && (
                        <View style={styles.imgLoader}>
                          <ActivityIndicator
                            size="small"
                            color={colors.primary}
                          />
                        </View>
                      )}
                    </View>
                  </TouchableOpacity>
                </ImageBackground>
              </TouchableWithoutFeedback>
            );
          } else {
            return (
              <TouchableWithoutFeedback
                onPress={() => {
                  if (proof.url) {
                    Linking.openURL(proof.url);
                  }
                }}
              >
                <View style={styles.imgBg}>
                  <TouchableOpacity
                    onPress={() => remove(index)}
                    style={styles.imgContainer}
                  >
                    <View>
                      <CloseIcon width={10} height={10} />
                      {proof.source && !proof.url && (
                        <View style={styles.imgLoader}>
                          <ActivityIndicator
                            size="small"
                            color={colors.primary}
                          />
                        </View>
                      )}
                    </View>
                  </TouchableOpacity>
                  <Play />
                </View>
              </TouchableWithoutFeedback>
            );
          }
        })}
      </View>
      {proofMap.length < 4 ? (
        <TouchableOpacity style={styles.uploadContainer}>
          <View style={styles.uploadView}>
            <View style={styles.uploadOptionContainer}>
              <TouchableOpacity
                onPress={() => onAttachmentUpload("camera")}
                style={styles.uploadOption}
              >
                <Camera2 width={30} height={30} />
                <Text>{t(`${PATH}/camera`)}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => onAttachmentUpload("audio")}
                style={styles.uploadOption}
              >
                <Microphone2 width={30} height={30} />
                <Text>{t(`${PATH}/audio`)}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  proofContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  imgBg: {
    height: 60,
    width: 60,
    marginRight: 10,
    marginBottom: 15,
    borderRadius: 6,
    borderColor: colors.black02,
    backgroundColor: "white",
    alignItems: "center",
    justifyContent: "center",
    borderStyle: "solid",
  },
  imgContainer: {
    position: "absolute",
    right: -7,
    top: -7,
    backgroundColor: colors.extraLightBlack,
    borderRadius: 15,
    borderColor: colors.Gray,
    borderWidth: 1,
    width: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 5,
  },
  imgLoader: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.lighterBlack,
    justifyContent: "center",
    alignItems: "center",
  },
  uploadContainer: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: colors.DarkGray,
    borderRadius: 5,
  },
  uploadView: {
    padding: 10,
  },
  uploadText: { color: colors.DarkerGray, marginTop: 5 },
  uploadOptionContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-evenly",
  },
  uploadOption: {
    alignItems: "center",
    flex: 1,
    marginHorizontal: 20,
    backgroundColor: colors.Whiteish,
    borderWidth: 1,
    borderColor: colors.DarkGray,
    padding: 5,
    borderRadius: 5,
  },
});

export default UploadProof;
